import {
  AUTH_CODE_KEYBOARD_CLEARANCE,
  getAuthFieldScrollTop,
  getRegistrationFieldScrollTop,
} from "./authKeyboardLayout";

export function getAuthKeyboardScrollTarget({
  field,
  scroller,
  viewportTop,
  viewportHeight,
  padding = 20,
  topInset = 0,
}) {
  const safeTopInset = Math.max(0, Number(topInset) || 0);
  // The registration scroller paints into the status-bar area. Focused fields
  // still need to stay below it; remove its scrolling padding from the usable
  // viewport and content height to preserve the existing scroll destinations.
  viewportTop += safeTopInset;
  viewportHeight = Math.max(0, viewportHeight - safeTopInset);
  const fieldRect = field.getBoundingClientRect();
  const metrics = {
    scrollTop: scroller.scrollTop,
    viewportTop,
    viewportHeight,
    fieldTop: fieldRect.top,
    fieldHeight: fieldRect.height,
    scrollHeight: Math.max(0, scroller.scrollHeight - safeTopInset),
    padding: field.autocomplete === "one-time-code"
      ? AUTH_CODE_KEYBOARD_CLEARANCE
      : padding,
  };
  const registration = field.closest('[data-testid="stable-mobile-web-register"]');
  const actions = registration?.querySelector('[data-auth-keyboard-context-end="register-form"]');
  const fields = actions
    ? Array.from(registration.querySelectorAll("[data-auth-field]"))
      .filter((input) => !input.disabled && !input.readOnly && input.type !== "hidden")
    : [];
  const fieldIndex = fields.indexOf(field);
  if (fieldIndex >= 0) {
    const firstFieldRect = fields[0].getBoundingClientRect();
    return getRegistrationFieldScrollTop({
      ...metrics,
      firstFieldTop: firstFieldRect.top,
      firstFieldHeight: firstFieldRect.height,
      actionsBottom: actions.getBoundingClientRect().bottom,
      fieldIndex,
      fieldCount: fields.length,
    });
  }

  const login = field.closest('[data-testid="stable-mobile-web-login"]');
  if (login) {
    const loginFields = Array.from(login.querySelectorAll("[data-auth-field]"));
    const fieldsAlreadyVisible = loginFields.every((input) => {
      const rect = input.getBoundingClientRect();
      return rect.top >= viewportTop + padding &&
        rect.bottom <= viewportTop + viewportHeight - padding;
    });
    // Do not move two readable login fields just to reposition the submit
    // button after QuickType changes. On compact screens, scroll only when a
    // field actually needs to clear the keyboard.
    if (fieldsAlreadyVisible) return metrics.scrollTop;
  }
  const forgot = field.closest('[data-testid="stable-mobile-web-forgot"]');
  const context = login || forgot;
  const contextEnd = context?.querySelector("[data-auth-keyboard-context-end]");
  if (contextEnd) {
    const firstField = context.querySelector("[data-auth-field]");
    const contextTop = firstField?.getBoundingClientRect().top ?? fieldRect.top;
    const contextBottom = contextEnd.getBoundingClientRect().bottom;
    if (contextBottom - contextTop + 2 * padding <= viewportHeight) {
      // Use the same complete group for both login fields. Otherwise returning
      // to username can undo the password's scroll and cause a visible nudge.
      return getAuthFieldScrollTop({
        ...metrics,
        fieldTop: contextTop,
        fieldHeight: contextBottom - contextTop,
      });
    }
  }
  return getAuthFieldScrollTop(metrics);
}

export function createAuthFieldScroller() {
  let animation = null;

  const cancel = () => {
    if (animation) window.cancelAnimationFrame(animation.frame);
    animation = null;
  };

  const scroll = (scroller, top, { smooth = false } = {}) => {
    if (animation?.scroller === scroller && Math.abs(animation.top - top) < 1) return;
    cancel();
    const startTop = scroller.scrollTop;
    // A no-op scroll can still interrupt WebKit's native focus/keyboard work.
    if (Math.abs(startTop - top) < 1) return;
    if (!smooth || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      scroller.scrollTo({ top, left: 0, behavior: "auto" });
      return;
    }

    const current = { scroller, top, frame: 0, startTime: null };
    animation = current;
    const step = (now) => {
      if (animation !== current) return;
      if (current.startTime === null) current.startTime = now;
      const progress = Math.min(1, (now - current.startTime) / 220);
      const eased = 1 - Math.pow(1 - progress, 3);
      scroller.scrollTo({ top: startTop + (top - startTop) * eased, left: 0, behavior: "auto" });
      if (progress < 1) current.frame = window.requestAnimationFrame(step);
      else animation = null;
    };
    current.frame = window.requestAnimationFrame(step);
  };

  return { scroll, cancel };
}
